import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { FINANCE_CURRENCIES } from '~/consts/currencies';

import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';

import { filterFlashRoomRates } from './autofillPackage';
import type { OfferBuilderPackage } from './types';
import { createDraftOfferBuilderPackage, getRoomRateLabel, parseNumberInput } from './utils';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (offerPackage: OfferBuilderPackage) => void;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

const DEFAULT_CURRENCY = 'AUD';

export default function CreateOfferPackageDialog({ open, onClose, onCreate, propertyRoomTypes = [] }: Props) {
  const [name, setName] = useState('');
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_CURRENCY);
  const [amount, setAmount] = useState('');
  const [numberOfNights, setNumberOfNights] = useState('');
  const [roomTypeId, setRoomTypeId] = useState('');
  const [ratePlanId, setRatePlanId] = useState('');
  const selectedRoomType = propertyRoomTypes.find((roomType) => roomType.id === roomTypeId);
  const flashRoomRates = filterFlashRoomRates(selectedRoomType?.room_rates ?? []);
  const parsedAmount = parseNumberInput(amount);
  const parsedNumberOfNights = parseNumberInput(numberOfNights);
  const hasInvalidAmount = parsedAmount !== undefined && parsedAmount < 1;
  const hasInvalidNumberOfNights =
    numberOfNights.length > 0 && (parsedNumberOfNights === undefined || parsedNumberOfNights < 1);
  const isCreateDisabled =
    name.trim().length === 0 ||
    baseCurrency.length === 0 ||
    parsedAmount === undefined ||
    hasInvalidAmount ||
    numberOfNights.length === 0 ||
    hasInvalidNumberOfNights ||
    roomTypeId.length === 0 ||
    ratePlanId.length === 0;

  useEffect(() => {
    if (open) {
      setName('');
      setBaseCurrency(DEFAULT_CURRENCY);
      setAmount('');
      setNumberOfNights('');
      setRoomTypeId('');
      setRatePlanId('');
    }
  }, [open]);

  const handleCreate = () => {
    if (isCreateDisabled) {
      return;
    }

    onCreate(
      createDraftOfferBuilderPackage({
        amount: parsedAmount ?? 0,
        baseCurrency,
        name,
        numberOfNights: parsedNumberOfNights ?? 0,
        ratePlanId,
        roomTypeId,
      }),
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create package</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            size="small"
            fullWidth
            onChange={(event) => setName(event.target.value)}
          />

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <TextField
              label="Base currency"
              value={baseCurrency}
              select
              size="small"
              fullWidth
              onChange={(event) => setBaseCurrency(event.target.value)}
            >
              {FINANCE_CURRENCIES.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              size="small"
              fullWidth
              error={hasInvalidAmount}
              helperText={hasInvalidAmount ? 'Amount must be greater than 0' : undefined}
              slotProps={{ formHelperText: { sx: { ml: 0 } }, htmlInput: { min: 1 } }}
              onChange={(event) => setAmount(event.target.value)}
            />
            <TextField
              label="Number of nights"
              type="number"
              value={numberOfNights}
              size="small"
              fullWidth
              error={hasInvalidNumberOfNights}
              helperText={hasInvalidNumberOfNights ? 'Number of nights must be greater than 0' : undefined}
              slotProps={{ formHelperText: { sx: { ml: 0 } }, htmlInput: { min: 1 } }}
              onChange={(event) => setNumberOfNights(event.target.value)}
            />
            <TextField
              label="Room type"
              value={roomTypeId}
              select
              size="small"
              fullWidth
              onChange={(event) => {
                setRoomTypeId(event.target.value);
                setRatePlanId('');
              }}
            >
              <MenuItem value="" disabled>
                Select room type
              </MenuItem>
              {propertyRoomTypes.map((roomType) => (
                <MenuItem key={roomType.id} value={roomType.id}>
                  {roomType.name || roomType.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Rate plan"
              value={ratePlanId}
              select
              size="small"
              fullWidth
              disabled={!roomTypeId || flashRoomRates.length === 0}
              onChange={(event) => setRatePlanId(event.target.value)}
            >
              <MenuItem value="" disabled>
                Select rate plan
              </MenuItem>
              {flashRoomRates.map((roomRate) => (
                <MenuItem key={roomRate.id} value={roomRate.id}>
                  {getRoomRateLabel(roomRate)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={isCreateDisabled}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
